import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type ConfigPackage, type ConfigRelease } from "@/lib/forex-api";

const statusChip: Record<string, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-500/10 text-emerald-500", label: "已发布" },
  superseded: { cls: "bg-text-muted/10 text-text-secondary", label: "已替代" },
  rolled_back: { cls: "bg-amber-500/10 text-amber-500", label: "已回滚" },
  failed: { cls: "bg-red-500/10 text-red-500", label: "发布失败" },
};

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "--";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

function stringifyPayload(payload: Record<string, unknown>) {
  return JSON.stringify(payload, null, 2);
}

export default function ConfigReleasesDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const releaseId = searchParams.get("id") || "";
  const [release, setRelease] = useState<ConfigRelease | null>(null);
  const [pkg, setPkg] = useState<ConfigPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([forexApi.listConfigReleases(), forexApi.listConfigPackages()])
      .then(([releaseResult, packageResult]) => {
        if (cancelled) return;
        const matched = releaseResult.items.find((item) => item.release_id === releaseId) || null;
        setRelease(matched);
        setPkg(matched ? packageResult.items.find((item) => item.config_key === matched.config_key) || null : null);
        if (!matched) setError("未找到配置发布记录，请从发布记录列表重新进入。");
      })
      .catch(() => {
        if (!cancelled) setError("配置发布详情接口不可用，请稍后重试。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [releaseId]);

  const handleRollback = async () => {
    if (!release) return;
    setRolling(true);
    try {
      const updated = await forexApi.rollbackConfigRelease(release.release_id);
      setRelease(updated);
      setToast({ type: "success", msg: "配置发布记录已回滚" });
    } catch {
      setToast({ type: "error", msg: "回滚失败，请确认后端服务和登录态" });
    } finally {
      setRolling(false);
    }
  };

  const st = release ? statusChip[release.status] || { cls: "bg-surface text-text-secondary", label: release.status } : null;

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "配置中心", href: "/forex/config" }, { label: "配置发布详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-text-primary">配置发布详情</h2>
            {release && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${st?.cls}`}>{st?.label}</span>}
            {release && <span className="text-[10px] text-text-muted font-mono">{release.release_id}</span>}
          </div>
          <p className="text-xs text-text-muted mt-0.5">{release ? `${release.config_key} · ${release.version} · ${formatTime(release.published_at)}` : releaseId || "未选择发布记录"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {release && release.status === "active" && <button onClick={handleRollback} disabled={rolling} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-50 whitespace-nowrap">{rolling ? <><i className="ri-loader-4-line animate-spin text-sm" />回滚中</> : <><i className="ri-history-line text-sm" />回滚</>}</button>}
          {release && <Link to={`/forex/config/detail?type=${release.config_key}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-settings-3-line text-sm" />配置详情</Link>}
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回列表</button>
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && release && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">配置包</p><p className="text-xs font-semibold mt-1 text-text-primary">{pkg?.label || release.config_key}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">版本</p><p className="text-lg font-semibold font-mono text-text-primary">{release.version}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">发布时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(release.published_at)}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">回滚时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(release.rolled_back_at)}</p></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">发布内容</h3>
              <div className="rounded-lg bg-surface/50 border border-surface-border overflow-hidden">
                <pre className="max-h-[420px] overflow-auto p-4 text-[11px] leading-relaxed text-text-primary font-mono">{stringifyPayload(release.payload)}</pre>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">影响范围</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-text-muted">C 端 API</span><span className="text-text-primary font-mono text-right">{pkg?.api_path || "GET /app/config/bootstrap"}</span></div>
                <div className="flex justify-between text-xs"><span className="text-text-muted">配置状态</span><span className="text-text-primary">{pkg?.status || "--"}</span></div>
                <div className="flex justify-between text-xs"><span className="text-text-muted">当前包版本</span><span className="text-text-primary font-mono">{pkg?.version || "--"}</span></div>
                <div className="flex justify-between text-xs"><span className="text-text-muted">发布说明</span><span className="text-text-primary text-right">{release.description || "--"}</span></div>
              </div>
            </div>
          </div>
        </>
      )}

      {toast && (<div className="fixed bottom-6 right-6 z-50"><div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}><i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}</div></div>)}
    </div>
  );
}
